import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  LifebuoyIcon,
  CubeIcon,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../../contexts/AuthContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { activityLogAPI } from '../../../services/activityTracker';
import { siteConfigAPI } from '../../../services/api';

export interface BannerData {
  id: number;
  message: string;
  trigger_type: 'always' | 'days_before_expiration' | 'after_expiration' | 'first_login';
  trigger_days: number;
  background_color: string;
  text_color: string;
  is_dismissible: boolean;
  button_text?: string;
  button_url?: string;
  button_bg_color?: string;
  button_text_color?: string;
}

function filterBanners(
  banners: BannerData[],
  daysRemaining?: number | null,
  isExpired?: boolean,
): BannerData[] {
  return banners.filter(b => {
    if (b.trigger_type === 'always') return true;
    if (b.trigger_type === 'first_login') return true;
    if (b.trigger_type === 'days_before_expiration') {
      if (daysRemaining != null) {
        return daysRemaining <= b.trigger_days && !isExpired;
      }
      // No expiration date set: show the banner anyway
      return true;
    }
    if (b.trigger_type === 'after_expiration') {
      return !!isExpired;
    }
    return false;
  });
}

export const useCompanyHeader = () => {
  const { user, logout } = useAuth();
  const { getSiteName } = useSiteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [lastLoginTime, setLastLoginTime] = useState<string | null>(null);
  const [allBanners, setAllBanners] = useState<BannerData[]>([]);

  const banners = filterBanners(allBanners, user?.days_remaining, user?.is_expired);

  useEffect(() => {
    siteConfigAPI.getBanners()
      .then(res => setAllBanners(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role === 'company') {
      activityLogAPI.getMyLastLogin()
        .then(res => {
          if (res.data.timestamp) setLastLoginTime(res.data.timestamp);
        })
        .catch(() => {});
    }
  }, [user]);

  const formatLastLogin = (dateStr?: string | null) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const handleLogout = () => {
    logout();
    navigate('/customer-login');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleDismissBanner = (bannerId: number) => {
    siteConfigAPI.dismissBanner(bannerId).catch(() => {});
    setAllBanners(prev => prev.filter(b => b.id !== bannerId));
  };

  const desktopButtonStyle = {
    borderRadius: '999px',
    padding: '8px 20px',
    borderWidth: '2px',
    fontWeight: '500' as const,
    fontSize: '0.9rem'
  };

  const mobileNavItems = [
    { path: '/company/dashboard', icon: HomeIcon, label: 'Home' },
    { path: '/company/customers', icon: UserGroupIcon, label: 'Customers' },
    { path: '/company/products', icon: CubeIcon, label: 'Products' },
    { path: '/company/orders', icon: ClipboardDocumentListIcon, label: 'Orders' },
    { path: '/company/support', icon: LifebuoyIcon, label: 'Support' },
    { path: '/company/settings', icon: Cog6ToothIcon, label: 'More' },
  ].filter(item => {
    // Owners see everything; only filter for sub-admins
    if (!user?.is_company_admin || user?.is_company_owner) return true;
    return true;
  });

  return {
    user,
    getSiteName,
    navigate,
    lastLoginTime,
    banners,
    formatLastLogin,
    handleLogout,
    isActive,
    desktopButtonStyle,
    mobileNavItems,
    handleDismissBanner,
  };
};
